import { useState } from 'react';
import { parseEnvelope, openAsOwner, openAsRecipient, describeEnvelope, type Envelope } from '../lib/envelope';

export function Open() {
  const [envelope, setEnvelope] = useState<Envelope | null>(null);
  const [credential, setCredential] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [openedName, setOpenedName] = useState<string | null>(null);

  async function load(file: File) {
    setError(null);
    setDone(false);
    try {
      setEnvelope(parseEnvelope(await file.text()));
    } catch (e) {
      setEnvelope(null);
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function unlock() {
    if (!envelope) return;
    setError(null);
    setBusy(true);
    try {
      // A key string is AG-prefixed; anything else is treated as a passphrase.
      const value = credential.trim();
      const opened = /^AG-/i.test(value)
        ? await openAsRecipient(envelope, value)
        : await openAsOwner(envelope, value);

      const url = URL.createObjectURL(opened.blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = opened.meta.name;
      a.click();
      URL.revokeObjectURL(url);
      setOpenedName(opened.meta.name);
      setDone(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <section className="card">
        <h2>1 · The envelope</h2>
        <input
          type="file"
          accept=".json,application/json"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void load(f);
          }}
        />
        {envelope && <EnvelopeSummary envelope={envelope} />}
      </section>

      {envelope && (
        <section className="card">
          <h2>2 · Your key or passphrase</h2>
          <p className="muted">
            Recipients have a key like <code>AG-XXXX-XXXX-XXXX-XXXX</code>. The
            owner uses their passphrase.
          </p>
          <input
            type="password"
            value={credential}
            onChange={(e) => setCredential(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void unlock()}
            placeholder="Key or passphrase"
          />
          {error && <p className="error">{error}</p>}
          {done && <p className="ok">Decrypted and downloaded{openedName ? ` — ${openedName}` : ''}.</p>}
          <button className="primary" disabled={busy || !credential} onClick={() => void unlock()}>
            {busy ? 'Decrypting…' : 'Open'}
          </button>
        </section>
      )}
    </>
  );
}

/**
 * What we can honestly say before the envelope is open.
 *
 * A v2 envelope encrypts its own filename and recipient list, so there is
 * genuinely nothing to show but the shape of it. Saying so is the point — it is
 * the property that makes the file safe to leave in a public place.
 */
function EnvelopeSummary({ envelope }: { envelope: Envelope }) {
  const d = describeEnvelope(envelope);
  if (d.name) {
    return (
      <p className="muted">
        {d.name} — sealed {new Date(d.createdAt!).toLocaleDateString()}, opens for{' '}
        {d.recipientCount} recipient{d.recipientCount === 1 ? '' : 's'}
        <br />
        <span className="warn-inline">Older format: this envelope's filename and recipients are readable without a key.</span>
      </p>
    );
  }
  return (
    <p className="muted">
      Encrypted envelope, opens for {d.recipientCount} recipient
      {d.recipientCount === 1 ? '' : 's'}. The filename and who it is for are
      encrypted too — they appear once it is open.
    </p>
  );
}
